import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronUp, LogOut, User } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { useHomeAuth, type HomeUser } from '@/contexts/HomeAuthContext'

/**
 * Signed-in account control for the site bar.
 *
 * A component rather than markup inside HomeShell because the bar is rendered
 * **twice** — once in the page flow and once inside the fixed strip that slides
 * down past the fold. Sharing one `open` flag and one ref between those two
 * copies opened both menus at once and pointed outside-click detection at
 * whichever copy mounted last. Each instance owns its own state here.
 */
export default function AccountMenu({ user }: { user: HomeUser }) {
  const { T } = useLanguage()
  const { logout } = useHomeAuth()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const itemClass =
    'flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-semibold ' +
    'text-foreground/80 transition-colors hover:bg-white/10 hover:text-foreground'

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-sm border border-white/12 bg-white/5 px-2 py-1 transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <img src={user.discord.avatar_url} alt="" className="h-5 w-5 rounded-full" />
        <span className="hidden max-w-[9rem] truncate text-[11px] font-bold tracking-wider text-foreground/80 sm:inline">
          {user.discord.name}
        </span>
        {open
          ? <ChevronUp className="h-3 w-3 text-muted-foreground" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1.5 w-56 overflow-hidden rounded-sm border border-white/12 bg-[#16161a] shadow-xl"
        >
          <div className="border-b border-white/10 px-3 py-2.5">
            <p className="truncate text-[13px] font-semibold leading-tight">{user.discord.name}</p>
            <p className="truncate text-[11px] text-muted-foreground">@{user.discord.username}</p>
          </div>
          <Link to="/profile" role="menuitem" onClick={() => setOpen(false)} className={itemClass}>
            <User className="h-3.5 w-3.5 shrink-0" />
            {T.profile.navLabel}
          </Link>
          <button
            role="menuitem"
            onClick={() => { setOpen(false); void logout() }}
            className={itemClass}
          >
            <LogOut className="h-3.5 w-3.5 shrink-0" />
            {T.profile.signOut}
          </button>
        </div>
      )}
    </div>
  )
}
